// Google Analytics 4 Integration
import environment from '../config/environment';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize Google Analytics
export const initializeAnalytics = (): void => {
  if (!environment.FEATURES.ANALYTICS || !environment.SERVICES.GOOGLE_ANALYTICS_ID) {
    console.log('Analytics disabled or no GA ID configured');
    return;
  }

  const GA_ID = environment.SERVICES.GOOGLE_ANALYTICS_ID;

  // Add gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    page_path: window.location.pathname,
    cookie_flags: 'SameSite=None;Secure',
  });

  console.log('Google Analytics initialized with ID:', GA_ID);
};

// Track page views
export const trackPageView = (path: string, title?: string): void => {
  if (!environment.FEATURES.ANALYTICS || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  params?: {
    category?: string;
    label?: string;
    value?: number;
    [key: string]: any;
  }
): void => {
  if (!environment.FEATURES.ANALYTICS || !window.gtag) return;

  window.gtag('event', eventName, {
    event_category: params?.category || 'engagement',
    event_label: params?.label,
    value: params?.value,
    ...params,
  });
};

// Predefined event trackers
export const analytics = {
  // User events
  trackLogin: () => trackEvent('login', { category: 'user' }),
  trackSignup: () => trackEvent('sign_up', { category: 'user' }),
  trackLogout: () => trackEvent('logout', { category: 'user' }),

  // Stock/Instrument events
  trackStockView: (symbol: string, name: string) =>
    trackEvent('view_item', {
      category: 'stock',
      label: symbol,
      item_name: name,
    }),

  trackAddToWatchlist: (symbol: string) =>
    trackEvent('add_to_wishlist', {
      category: 'stock',
      label: symbol,
    }),

  // Chart events
  trackChartInteraction: (symbol: string, range: string) =>
    trackEvent('chart_interaction', {
      category: 'chart',
      label: `${symbol}_${range}`,
    }),

  // Comment events
  trackComment: (symbol: string) =>
    trackEvent('comment', {
      category: 'engagement',
      label: symbol,
    }),

  // Search events
  trackSearch: (query: string) =>
    trackEvent('search', {
      category: 'engagement',
      search_term: query,
    }),

  // Navigation events
  trackNavigation: (destination: string) =>
    trackEvent('navigation', {
      category: 'navigation',
      label: destination,
    }),
};

export default analytics;
